import React, { useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonToolbar,
  isPlatform
} from '@ionic/react';
import styles from './PreShippingQuestionnaire.module.css';
import {
  PreShippingSurveyPayload,
  usePreShippingSurveyMutation
} from '../query/preShippingSurveyMutation';
import { useHistory, useLocation } from 'react-router-dom';
import { closeOutline } from 'ionicons/icons';

const initialState: PreShippingSurveyPayload = {
  orderName: '',
  addressConfirmed: false,
  catFlap: '',
  catFlapDetails: '',
  catCount: '',
  catCountOther: '',
  preyAttempts: '',
  catWeightStatus: '',
  catWeightNotes: '',
  powerWifi: '',
  accessories: '',
  additionalNotes: ''
};

const PreShippingQuestionnaire: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [formData, setFormData] = useState<PreShippingSurveyPayload>(() => {
    const params = new URLSearchParams(location.search);

    return {
      ...initialState,
      orderName: params.get('orderName') ?? ''
    };
  });
  const { mutateAsync, isPending, isSuccess, isError, error } = usePreShippingSurveyMutation();
  const isNativeApp = isPlatform('capacitor');

  const handleClose = () => {
    history.push('/');
  };

  const handleChange =
    (field: keyof PreShippingSurveyPayload) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { target } = event;
        const next = target instanceof HTMLInputElement && target.type === 'checkbox'
          ? target.checked
          : target.value;
        setFormData((prev) => ({
          ...prev,
          [field]: next
        }));
      };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await mutateAsync(formData);
  };

  return (
    <IonPage className={styles.page}>
      {isNativeApp && (
        <IonHeader translucent>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={handleClose}>
                <IonIcon slot="start" icon={closeOutline} />
                Close
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
      )}
      <IonContent fullscreen className={styles.content}>
        <div className={styles.card}>
          <div className={styles.header}>
            <img
              src="/assets/logo/logo-horizontal.svg"
              alt="OnlyCat logo"
              className={styles.logo}
            />
            <h1 className={styles.title}>OnlyCat - Delivery Questionnaire</h1>
          </div>

          {isSuccess && (
            <div className={`${styles.banner} ${styles.bannerSuccess}`}>
              Thanks for your order and for completing the questionnaire. We&apos;re working hard to deliver your OnlyCat order. We&apos;ll be in touch if we have any questions; otherwise, you&apos;ll receive tracking details once your order has been dispatched.
            </div>
          )}

          {isError && (
            <div className={`${styles.banner} ${styles.bannerError}`}>
              {error instanceof Error ? error.message : 'Something went wrong. Please try again.'}
            </div>
          )}

          {!isSuccess && (
            <>
              <p className={styles.intro}>
                Thank you again for your OnlyCat order. As we gear up for shipment we have a few questions so we can support you as best we can.
                Please take a moment to confirm the details below.
              </p>

              <hr className={styles.divider} />
            </>
          )}

          {!isSuccess && (
            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.field}>
                <label className={styles.labelRow}>
                  Please confirm the name on your order.
                  <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Full name"
                  value={formData.orderName}
                  onChange={handleChange('orderName')}
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.labelRow}>
                  Please ensure your delivery address on your order confirmation is correct.
                  <span className={styles.required}>*</span>
                </label>
                <label className={styles.optionLabel}>
                  <input
                    type="checkbox"
                    checked={formData.addressConfirmed}
                    onChange={handleChange('addressConfirmed')}
                    required
                  />
                  I confirm the delivery address listed is correct.
                </label>
              </div>

              <div className={styles.field}>
                <label className={styles.labelRow}>
                  Do you currently have a cat flap? If yes, which make and model?
                  <span className={styles.required}>*</span>
                </label>
                <div className={styles.options}>
                  <label className={styles.optionLabel}>
                    <input
                      type="radio"
                      name="catFlap"
                      value="no"
                      checked={formData.catFlap === 'no'}
                      onChange={handleChange('catFlap')}
                      required
                    />
                    No
                  </label>
                  <label className={styles.optionLabel}>
                    <input
                      type="radio"
                      name="catFlap"
                      value="yes"
                      checked={formData.catFlap === 'yes'}
                      onChange={handleChange('catFlap')}
                      required
                    />
                    Yes
                  </label>
                </div>
                {formData.catFlap === 'yes' && (
                  <input
                    type="text"
                    className={`${styles.input} ${styles.subInput}`}
                    placeholder="Let us know the make and model"
                    value={formData.catFlapDetails}
                    onChange={handleChange('catFlapDetails')}
                  />
                )}
              </div>

              <div className={styles.field}>
                <label className={styles.labelRow}>
                  How many cats are in your household?
                  <span className={styles.required}>*</span>
                </label>
                <div className={styles.options}>
                  {['1', '2', '3'].map((option) => (
                    <label key={option} className={styles.optionLabel}>
                      <input
                        type="radio"
                        name="catCount"
                        value={option}
                        checked={formData.catCount === option}
                        onChange={handleChange('catCount')}
                        required
                      />
                      {option}
                    </label>
                  ))}
                  <label className={styles.optionLabel}>
                    <input
                      type="radio"
                      name="catCount"
                      value="other"
                      checked={formData.catCount === 'other'}
                      onChange={handleChange('catCount')}
                      required
                    />
                    Other
                  </label>
                </div>
                {formData.catCount === 'other' && (
                  <input
                    type="text"
                    className={`${styles.input} ${styles.subInput}`}
                    placeholder="Tell us how many cats you have"
                    value={formData.catCountOther}
                    onChange={handleChange('catCountOther')}
                  />
                )}
              </div>

              <div className={styles.field}>
                <label className={styles.labelRow}>
                  In a typical month, how often will your cats attempt to bring home prey?
                  <span className={styles.required}>*</span>
                </label>
                <textarea
                  className={styles.textarea}
                  placeholder="Share any helpful context"
                  value={formData.preyAttempts}
                  onChange={handleChange('preyAttempts')}
                  required
                />
              </div>

            <div className={styles.field}>
              <label className={styles.labelRow}>
                Please confirm that your cat(s) is/are all less than 6kg.
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.options}>
                <label className={styles.optionLabel}>
                  <input
                    type="radio"
                    name="catWeightStatus"
                    value="under"
                    checked={formData.catWeightStatus === 'under'}
                    onChange={handleChange('catWeightStatus')}
                    required
                  />
                  Yes
                </label>
                <label className={styles.optionLabel}>
                  <input
                    type="radio"
                    name="catWeightStatus"
                    value="over"
                    checked={formData.catWeightStatus === 'over'}
                    onChange={handleChange('catWeightStatus')}
                    required
                  />
                  No - some are more than 6kg. Please confirm their weight(s).
                </label>
              </div>
              {formData.catWeightStatus === 'over' && (
                <textarea
                  className={`${styles.textarea} ${styles.subInput}`}
                  placeholder="Share the weight(s) so we can confirm compatibility"
                  value={formData.catWeightNotes}
                  onChange={handleChange('catWeightNotes')}
                />
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.labelRow}>
                Please confirm that you understand OnlyCat requires mains power and WiFi.
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.options}>
                {[
                  { value: 'confirm', label: 'I confirm' },
                  { value: 'can-work', label: "I didn’t know this - but I can make it work." },
                  { value: 'contact', label: "I didn’t know this - it’s an issue. (Please contact us)" },
                  { value: 'power-no-wifi', label: 'I have power, but not WiFi. (Please contact us)' }
                ].map((option) => (
                  <label key={option.value} className={styles.optionLabel}>
                    <input
                      type="radio"
                      name="powerWifi"
                      value={option.value}
                      checked={formData.powerWifi === option.value}
                      onChange={handleChange('powerWifi')}
                      required
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.labelRow}>
                Have you ordered any accessories (glass/mounting or tunnel adaptors)? Note: OnlyCat is compatible with SureFlap Cat Flap accessories.
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.options}>
                {[
                  { value: 'none', label: "No - I don't need any." },
                  { value: 'order-from-us', label: "No - But I'd like to order some from you. (Please contact us if you require advice)" },
                  { value: 'already', label: "Yes - I've already ordered what I need." }
                ].map((option) => (
                  <label key={option.value} className={styles.optionLabel}>
                    <input
                      type="radio"
                      name="accessories"
                      value={option.value}
                      checked={formData.accessories === option.value}
                      onChange={handleChange('accessories')}
                      required
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.labelRow}>
                Is there anything else you&apos;d like to let us know?
              </label>
              <textarea
                className={styles.textarea}
                placeholder="Share any extra notes or requests"
                value={formData.additionalNotes}
                onChange={handleChange('additionalNotes')}
              />
            </div>

              <button
                type="submit"
                className={styles.submitButton}
                disabled={isPending}
              >
                {isPending ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          )}

          <div className={styles.footerMedia}>
            {!isSuccess && (
              <p className={styles.footerNote}>
                Thank you for your order and your support. We&apos;ve been working hard to design OnlyCat into the best product possible.
                We can&apos;t wait for you to receive your OnlyCat and to hear your stories.
              </p>
            )}
            <img
              src="/assets/images/box-contents-web.jpg"
              alt="OnlyCat product"
              className={styles.deviceImage}
            />
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PreShippingQuestionnaire;
